import { useParams } from "react-router-dom";
import { Calendar, Clock, Tag, Share2 } from "lucide-react";
import blogsData from "./BlogsData.json";

interface GalleryImage {
  src: string;
  alt: string;
}

interface BlogData {
  id: string;
  category: string;
  title: string;
  date: string;
  readTime: string;
  heroImage: string;
  heroAlt: string;
  body: string[];
  gallery: GalleryImage[];
  keyBenefitsTitle: string;
  keyBenefits: string[];
  cta: string;
  tags: string[];
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = (blogsData.blogs as BlogData[]).find((b) => b.id === slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-page)] font-sans">
        <div className="text-center px-4">
          <p className="text-lg font-semibold text-[var(--text-primary)]">Blog not found</p>
          <p className="text-sm text-[var(--text-muted)] mt-2">The article you’re looking for doesn’t exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-white)] font-sans text-[var(--text-primary)]">
      <section className="relative w-full overflow-hidden">
        <div className="relative h-[46vh] sm:h-[52vh] md:h-[62vh] lg:h-[70vh] overflow-hidden">
          <img
            src={post.heroImage}
            alt={post.heroAlt}
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/60" />
          <div className="relative h-full">
            <div className="container-page h-full flex items-end pb-14 sm:pb-16">
              <div className="max-w-2xl">
                <span className="inline-flex items-center rounded-full bg-[var(--primary-purple)] px-4 py-1 text-xs font-semibold text-white shadow-sm">
                  {post.category}
                </span>
                <h1 className="mt-6 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight text-white">
                  {post.title}
                </h1>
                <div className="mt-5 flex flex-wrap gap-4 text-sm text-white/90">
                  <span className="inline-flex items-center gap-2">
                    <Calendar size={16} className="opacity-80" />
                    {post.date}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Clock size={16} className="opacity-80" />
                    {post.readTime}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="container-page max-w-5xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="space-y-6 text-[var(--text-secondary)] leading-8">
          {post.body.map((paragraph, index) => (
            <p key={index} className="text-base sm:text-lg">
              {paragraph}
            </p>
          ))}
        </div>

        <section className="mt-16 grid gap-5 lg:grid-cols-[1.5fr_1fr]">
          <div className="rounded-[var(--radius-lg)] overflow-hidden shadow-sm bg-[var(--bg-page)] min-h-[360px]">
            <img
              src={post.gallery[0].src}
              alt={post.gallery[0].alt}
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="grid grid-rows-2 gap-5">
            <div className="rounded-[var(--radius-lg)] overflow-hidden shadow-sm bg-[var(--bg-page)] min-h-[172px]">
              <img
                src={post.gallery[1].src}
                alt={post.gallery[1].alt}
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="rounded-[var(--radius-lg)] overflow-hidden shadow-sm bg-[var(--bg-page)] min-h-[172px]">
              <img
                src={post.gallery[2].src}
                alt={post.gallery[2].alt}
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </section>

        <section className="mt-16 space-y-6">
          <p className="text-base font-semibold text-[var(--text-primary)]">{post.keyBenefitsTitle}</p>
          <ul className="space-y-3 list-disc list-inside text-[var(--text-secondary)]">
            {post.keyBenefits.map((benefit, index) => (
              <li key={index} className="text-base leading-7">
                {benefit}
              </li>
            ))}
          </ul>
          <p className="text-base font-semibold text-[var(--text-primary)]">{post.cta}</p>
        </section>

        <section className="mt-16 pt-8 border-t border-[var(--border-light)] flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <Tag size={15} className="text-[var(--text-muted)]" />
            <span className="text-sm font-medium text-[var(--text-secondary)]">Tags:</span>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-sm text-[var(--text-secondary)] border border-[var(--border-light)] rounded-full px-3 py-1"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <button className="inline-flex items-center gap-2 rounded-full border border-[var(--primary-purple)] px-5 py-2 text-sm font-medium text-[var(--primary-purple)] transition hover:bg-[var(--primary-purple)] hover:text-white">
            <Share2 size={14} />
            Share Article
          </button>
        </section>
      </main>
    </div>
  );
}
